/**
 * StaffWorkingHoursTab (11F.7) — structured weekly availability.
 *
 * Replaces the untyped `working_hours` map that nothing ever wrote. Blocks
 * are per-day with start/end times, and a day may hold MORE THAN ONE block
 * so a split shift (09:00–12:00 and 16:00–19:00) is expressible — the old
 * one-range-per-day dict could not represent that at all.
 *
 * These hours are the denominator behind the Timesheet tab's occupancy
 * figure, which is why the empty state says so: leaving this blank is not
 * cosmetic, it suppresses a metric.
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';

import { Button, Card, Dropdown, Input } from '@/components/common';
import { ApiError } from '@core/api/client';
import {
  useStaffMember,
  useUpdateStaff,
  type DayOfWeek,
  type WorkingHoursBlock,
} from '@features/people/api/staff';

const DAYS: { value: DayOfWeek; label: string }[] = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: flex-end;
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const Hint = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

interface Props {
  staffId: string;
  canEdit?: boolean;
}

export const StaffWorkingHoursTab: React.FC<Props> = ({ staffId, canEdit = true }) => {
  const { data: staff, isLoading } = useStaffMember(staffId);
  const update = useUpdateStaff();
  const [blocks, setBlocks] = useState<WorkingHoursBlock[]>([]);

  useEffect(() => {
    if (staff) setBlocks(staff.working_hours_schedule ?? []);
  }, [staff]);

  const setBlock = (idx: number, patch: Partial<WorkingHoursBlock>) => {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };

  const handleSave = async () => {
    // Mirror the server's validator so the operator gets an inline message
    // rather than a 422 round-trip.
    for (const b of blocks) {
      if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(b.start_time) ||
          !/^([01]\d|2[0-3]):([0-5]\d)$/.test(b.end_time)) {
        toast.error('Times must be in 24-hour HH:MM format.');
        return;
      }
      if (b.end_time <= b.start_time) {
        toast.error('Each block must end after it starts.');
        return;
      }
    }
    try {
      await update.mutateAsync({
        id: staffId,
        body: { working_hours_schedule: blocks },
      });
      toast.success('Working hours saved.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save working hours.');
    }
  };

  if (isLoading) return <p style={{ color: '#64748b' }}>Loading working hours…</p>;

  return (
    <div data-testid="candeza-staff-working-hours-tab">
      <Card padding="lg">
        <Hint>
          Weekly availability. Add more than one block on a day for a split
          shift. These hours are the basis for the occupancy figure on the
          Timesheet tab.
        </Hint>

        {blocks.length === 0 ? (
          <p style={{ color: '#94a3b8' }} data-testid="candeza-staff-working-hours-empty">
            No working hours set. Occupancy cannot be calculated until at least
            one block is added.
          </p>
        ) : (
          blocks.map((b, idx) => (
            <Row key={`${b.day_of_week}-${idx}`} data-testid={`candeza-staff-wh-row-${idx}`}>
              <Dropdown
                label="Day"
                options={DAYS}
                value={b.day_of_week}
                onChange={(v) => setBlock(idx, { day_of_week: v as DayOfWeek })}
                testId={`candeza-staff-wh-day-${idx}`}
              />
              <Input
                label="From"
                type="time"
                value={b.start_time}
                disabled={!canEdit}
                onChange={(e) => setBlock(idx, { start_time: e.target.value })}
                data-testid={`candeza-staff-wh-start-${idx}`}
              />
              <Input
                label="To"
                type="time"
                value={b.end_time}
                disabled={!canEdit}
                onChange={(e) => setBlock(idx, { end_time: e.target.value })}
                data-testid={`candeza-staff-wh-end-${idx}`}
              />
              {canEdit && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setBlocks((prev) => prev.filter((_, i) => i !== idx))}
                  data-testid={`candeza-staff-wh-remove-${idx}`}
                >
                  Remove
                </Button>
              )}
            </Row>
          ))
        )}

        {canEdit && (
          <Actions>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setBlocks((prev) => [
                  ...prev,
                  { day_of_week: 'monday', start_time: '09:00', end_time: '17:00' },
                ])
              }
              data-testid="candeza-staff-wh-add"
            >
              Add block
            </Button>
            <Button
              onClick={handleSave}
              disabled={update.isPending}
              data-testid="candeza-staff-wh-save"
            >
              {update.isPending ? 'Saving…' : 'Save working hours'}
            </Button>
          </Actions>
        )}
      </Card>
    </div>
  );
};

export default StaffWorkingHoursTab;
